import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { CurrentUserClient } from '../web-api-client.ts';
import useToast from '../hooks/UseToast.js';
import BackButton from './BackButton';
import Loader from './Loader';

// User-controlled opt-in for inspection scan text alerts.
//
// COMPLIANCE — this page is the opt-in evidence submitted to Twilio for
// toll-free verification (see docs/sms-toll-free-verification.md). Do not:
//   - pre-check the consent checkbox,
//   - merge the consent into another form or make it a condition of service,
//   - remove the disclosure text (frequency, rates, STOP/HELP, terms link).
// The consent language itself comes from the server so the audit record
// always matches what was displayed.
const SmsAlertsPage = () => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preference, setPreference] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);

  const loadPreference = async () => {
    const client = new CurrentUserClient();
    const pref = await client.getSmsAlertPreference();
    setPreference(pref);
    setPhoneNumber(pref.phoneNumber || '');
    setConsentChecked(false);
  };

  useEffect(() => {
    (async () => {
      try {
        await loadPreference();
      } catch (err) {
        showToast('Failed to load your text alert settings.', 'error');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const phoneDigits = phoneNumber.replace(/\D/g, '');
  const phoneIsValid = phoneDigits.length === 10 || (phoneDigits.length === 11 && phoneDigits.startsWith('1'));

  const handleOptIn = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const client = new CurrentUserClient();
      await client.optInToSmsAlerts({ phoneNumber, consentGiven: consentChecked });
      await loadPreference();
      showToast('You are signed up for text alerts. A confirmation text is on its way.', 'success');
    } catch (err) {
      showToast('Failed to sign up: ' + (err.response || err.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleOptOut = async () => {
    setSaving(true);
    try {
      const client = new CurrentUserClient();
      await client.optOutOfSmsAlerts();
      await loadPreference();
      showToast('You have been unsubscribed from text alerts.', 'success');
    } catch (err) {
      showToast('Failed to unsubscribe: ' + (err.response || err.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="container mt-5" style={{ maxWidth: '640px' }}>
      <BackButton />
      <Card className="p-4">
        <h2>Inspection Scan Text Alerts</h2>
        <p className="text-muted">
          Get a text message from Q-Car the moment one of your vehicle inspection
          QR codes is scanned.
        </p>

        {preference?.optedIn ? (
          <>
            <Alert variant="success">
              Text alerts are <strong>on</strong> for{' '}
              <strong>{preference.phoneNumber}</strong>.
            </Alert>
            <p className="small text-muted">
              You can stop receiving alerts at any time by replying STOP to any
              message or by clicking the button below.
            </p>
            <Button variant="outline-danger" onClick={handleOptOut} disabled={saving}>
              {saving ? 'Saving...' : 'Stop Receiving Text Alerts'}
            </Button>
          </>
        ) : (
          <Form onSubmit={handleOptIn}>
            <Form.Group controlId="smsPhoneNumber" className="mb-3">
              <Form.Label>Mobile phone number</Form.Label>
              <Form.Control
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="(555) 555-5555"
                autoComplete="tel"
              />
              <Form.Text className="text-muted">US mobile numbers only.</Form.Text>
            </Form.Group>

            {/* Twilio requires an affirmative, unchecked-by-default action. */}
            <Form.Group controlId="smsConsent" className="mb-3">
              <Form.Check
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                label={preference?.consentText}
              />
            </Form.Group>

            <Button
              type="submit"
              variant="primary"
              disabled={saving || !consentChecked || !phoneIsValid}
            >
              {saving ? 'Signing Up...' : 'Sign Up for Text Alerts'}
            </Button>

            <p className="small text-muted mt-3 mb-0">
              Message frequency varies based on inspection activity. Message and
              data rates may apply. Reply STOP to cancel or HELP for help.
              Signing up for text alerts is optional and not required to use
              Q-Car. See our <Link to="/sms-terms">SMS Terms &amp; Privacy Policy</Link>.
            </p>
          </Form>
        )}
      </Card>
    </div>
  );
};

export default SmsAlertsPage;
